import React from "react";
import styled, { css } from "styled-components";
import { tokens } from "../../theme/tokens";

// StatusBadge / Pill — semantic status chip.
//
//   <StatusBadge tone="success">Approved</StatusBadge>
//   <StatusBadge status="pending">Pending</StatusBadge>   // auto-maps common strings
//
// tones: success | warn | danger | info | neutral | brand

const { status, radii, typography, spacing } = tokens;

// Map common domain status strings → semantic tone.
const STATUS_TONE = {
    // approvals / loans / vetting
    approved: "success", active: "success", settled: "success", enabled: "success",
    completed: "success", delivered: "success", paid: "success", verified: "success",
    online: "success", success: "success", accepted: "success",
    pending: "warn", processing: "warn", "in_review": "warn", review: "warn",
    "in review": "warn", ongoing: "warn", "in_transit": "warn", enroute: "warn",
    open: "warn", warning: "warn", repaying: "warn", late: "warn", "nearly late": "warn",
    rejected: "danger", failed: "danger", cancelled: "danger", canceled: "danger",
    declined: "danger", suspended: "danger", flagged: "danger", overdue: "danger",
    blocked: "danger", offline: "danger", disabled: "danger", bounced: "danger",
    inactive: "neutral", draft: "neutral", closed: "neutral", archived: "neutral",
    new: "info", info: "info", scheduled: "info", assigned: "info", triaged: "info",
};

export function toneForStatus(s) {
    if (!s) return "neutral";
    return STATUS_TONE[String(s).toLowerCase().trim()] || "neutral";
}

const dot = css`
    &::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
        flex-shrink: 0;
    }
`;

const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: ${spacing[2]};
    height: ${(p) => (p.$size === "sm" ? "20px" : "24px")};
    padding: 0 ${(p) => (p.$size === "sm" ? "8px" : "10px")};
    border-radius: ${radii.pill};
    font-family: ${typography.fonts.body};
    font-size: ${(p) => (p.$size === "sm" ? typography.size.xs : typography.size.sm)};
    font-weight: ${typography.weight.semibold};
    line-height: 1;
    letter-spacing: 0.01em;
    white-space: nowrap;
    color: ${(p) => (status[p.$tone] || status.neutral).fg};
    background: ${(p) => (status[p.$tone] || status.neutral).bg};
    border: 1px solid ${(p) => (status[p.$tone] || status.neutral).border};
    ${(p) => p.$dot && dot};
`;

export default function StatusBadge({
    tone,
    status: statusStr,
    dot = true,
    size = "md",
    children,
    ...rest
}) {
    const resolved = tone || toneForStatus(statusStr);
    return (
        <Pill $tone={resolved} $dot={dot} $size={size} {...rest}>
            {children != null ? children : statusStr}
        </Pill>
    );
}

// Plain chip (no semantic colour) for tags/filters.
export const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 12px;
    border-radius: ${radii.pill};
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.medium};
    color: ${tokens.colors.heading};
    background: ${tokens.colors.surfaceSunken};
    border: 1px solid ${tokens.colors.border};
`;
